import axios from 'axios';
import TokenManager from './tokenManager';

/**
 * Configure axios interceptors for JWT token management
 * 
 * @param {string} apiBaseUrl - The base URL for API calls (used for token refresh)
 * @param {AxiosInstance} axiosClient - Optional specific axios instance to setup interceptors on
 *                                       If not provided, uses the global axios instance
 */
export function setupTokenInterceptors(apiBaseUrl, axiosClient = null) {
  // Use provided client or default global axios instance
  const client = axiosClient || axios;
  
  // Request interceptor - add token to every request
  client.interceptors.request.use(
    (config) => {
      const authHeader = TokenManager.getAuthHeader();
      if (authHeader) {
        config.headers = config.headers || {};
        config.headers['Authorization'] = authHeader.Authorization;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - handle 401 (expired token) with refresh
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Check if error is 401 (Unauthorized)
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          // Try to refresh token
          const newAccessToken = await TokenManager.refreshAccessToken(apiBaseUrl);
          
          // Retry original request with new token
          originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
          return client(originalRequest);
        } catch (refreshError) {
          // Refresh failed - redirect to login
          TokenManager.clear();
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );
}

/**
 * Setup global API client with token management
 */
export function createApiClient(baseURL) {
  const client = axios.create({
    baseURL,
    timeout: 10000
  });

  // Add authorization header
  client.interceptors.request.use(
    (config) => {
      const authHeader = TokenManager.getAuthHeader();
      if (authHeader) {
        config.headers = config.headers || {};
        config.headers['Authorization'] = authHeader.Authorization;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Handle token refresh on 401
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          const newAccessToken = await TokenManager.refreshAccessToken(baseURL);
          originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
          return client(originalRequest);
        } catch (refreshError) {
          TokenManager.clear();
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );

  return client;
}

export default setupTokenInterceptors;
